import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.getMe()
        .then(data => {
          setUser(data);
          // Check if profile completion is required (only for students)
          const roles = typeof data.roles === 'string' ? JSON.parse(data.roles) : (data.roles || []);
          const isStudent = roles.includes('student') && !roles.includes('admin') && !roles.includes('lecturer');

          if (!data.is_profile_complete && isStudent) {
            navigate('/complete-profile', { replace: true });
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [navigate]);

  const login = async (identifier, password, rememberMe = false) => {
    // Password login removed - this is for OAuth only
    throw new Error('Password authentication has been disabled. Please use Google Sign-In.');
  };

  const googleSignIn = () => {
    // Initiate OAuth flow by redirecting to backend
    api.googleSignIn();
  };

  const handleOAuthCallback = async (code, state) => {
    try {
      const data = await api.googleCallback(code, state);
      localStorage.setItem('token', data.token);
      setUser(data.user);

      // Check if profile completion is required
      if (data.needs_profile) {
        return '/complete-profile';
      }

      // Return the intended path based on role
      const roles = typeof data.user.roles === 'string'
        ? JSON.parse(data.user.roles)
        : (data.user.roles || []);

      if (roles.includes('admin')) {
        return '/dashboard/admin/dashboard';
      } else if (roles.includes('lecturer')) {
        return '/dashboard/lecturer/dashboard';
      } else {
        return '/dashboard/student/dashboard';
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  };

  const register = async (email, password, name) => {
    // Registration removed - open registration via Google OAuth
    throw new Error('Registration is now done through Google Sign-In.');
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateProfile = (userData) => {
    setUser(userData);
  };

  // Helper to check if user has a specific role
  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
    return roles.includes(role);
  };

  // Check if user is admin
  const isAdmin = () => hasRole('admin');

  // Check if user is lecturer (or admin)
  const isLecturer = () => hasRole('lecturer') || hasRole('admin');

  // Check if user is student
  const isStudent = () => hasRole('student');

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        googleSignIn,
        handleOAuthCallback,
        register,
        logout,
        updateProfile,
        hasRole,
        isAdmin,
        isLecturer,
        isStudent
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
